import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { ChatService } from '../../@shared/services/chat.service';
import { ConversationResponse, MessageResponse } from '../../@shared/models/response.module';
import { AuthService } from '../../@core/services/auth.service';

@Component({
  selector: 'app-chat',
  standalone: false,
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.css'
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef;

  conversations: ConversationResponse[] = [];
  selectedConversation: ConversationResponse | null = null;
  messages: MessageResponse[] = [];
  newMessage: string = '';
  isLoadingConversations: boolean = false;
  isLoadingMessages: boolean = false;
  isSending: boolean = false;
  currentUserId: string;

  private pollingSubscription: Subscription | null = null;

  constructor(
    private chatService: ChatService,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  ngOnInit() {
    const token = this.authService.getToken();
    this.currentUserId = token?.user_id || '';
    this.loadConversations();
    this.startConversationListPolling();

    this.route.queryParams.subscribe(params => {
      if (params['conversation']) {
        const conversationId = parseInt(params['conversation'], 10);
        this.selectConversationById(conversationId);
      }
    });
  }

  private conversationListPollingSubscription: Subscription | null = null;

  startConversationListPolling() {
    this.stopConversationListPolling();
    this.conversationListPollingSubscription = interval(15000).subscribe(() => {
      this.refreshConversations();
    });
  }

  stopConversationListPolling() {
    if (this.conversationListPollingSubscription) {
      this.conversationListPollingSubscription.unsubscribe();
      this.conversationListPollingSubscription = null;
    }
  }

  ngOnDestroy() {
    this.stopMessagePolling();
    this.stopConversationListPolling();
  }

  loadConversations() {
    this.isLoadingConversations = true;
    this.chatService.getConversations({ limit: 50 }).subscribe({
      next: (data) => {
        this.conversations = data.data || [];
        this.isLoadingConversations = false;
      },
      error: () => {
        this.isLoadingConversations = false;
      }
    });
  }

  selectConversationById(id: number) {
    const conversation = this.conversations.find(c => c.conversation_id === id);
    if (conversation) {
      this.selectConversation(conversation);
    } else {
      this.chatService.getConversation({ conversation_id: id }).subscribe({
        next: (data) => {
          if (data.data) {
            this.selectConversation(data.data);
          }
        }
      });
    }
  }

  selectConversation(conversation: ConversationResponse) {
    this.selectedConversation = conversation;
    this.loadMessages();
    this.markAsRead();
    this.startMessagePolling();
  }

  loadMessages() {
    if (!this.selectedConversation) return;

    this.isLoadingMessages = true;
    this.chatService.getMessages({
      conversation_id: this.selectedConversation.conversation_id,
      limit: 100
    }).subscribe({
      next: (data) => {
        this.messages = data.data || [];
        this.isLoadingMessages = false;
        this.scrollToBottom();
      },
      error: () => {
        this.isLoadingMessages = false;
      }
    });
  }

  markAsRead() {
    if (!this.selectedConversation) return;

    this.chatService.markAsRead({
      conversation_id: this.selectedConversation.conversation_id
    }).subscribe({
      next: () => {
        if (this.selectedConversation) {
          this.selectedConversation.unread_count = 0;
        }
        this.chatService.refreshUnreadCount();
      }
    });
  }

  sendMessage() {
    if (!this.selectedConversation || !this.newMessage.trim()) return;

    this.isSending = true;
    const messageText = this.newMessage.trim();
    this.newMessage = '';

    this.chatService.sendMessage({
      conversation_id: this.selectedConversation.conversation_id,
      message_text: messageText
    }).subscribe({
      next: (data) => {
        if (data.data) {
          this.messages.push(data.data);
          this.scrollToBottom();
        }
        this.isSending = false;
      },
      error: () => {
        this.newMessage = messageText;
        this.isSending = false;
      }
    });
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  startMessagePolling() {
    this.stopMessagePolling();
    this.pollingSubscription = interval(15000).subscribe(() => {
      this.pollNewMessages();
      this.refreshConversations();
    });
  }

  refreshConversations() {
    this.chatService.getConversations({ limit: 50 }).subscribe({
      next: (data) => {
        this.conversations = data.data || [];
      }
    });
  }

  stopMessagePolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  pollNewMessages() {
    if (!this.selectedConversation) return;

    this.chatService.getMessages({
      conversation_id: this.selectedConversation.conversation_id,
      limit: 100
    }).subscribe({
      next: (data) => {
        const newMessages = data.data || [];
        if (newMessages.length > this.messages.length) {
          this.messages = newMessages;
          this.scrollToBottom();
          this.markAsRead();
        }
      }
    });
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.messagesContainer) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 100);
  }

  getConversationTitle(conversation: ConversationResponse): string {
    if (conversation.subject) {
      return conversation.subject;
    }
    if (conversation.event) {
      return conversation.event.event_name;
    }
    if (conversation.company) {
      return conversation.company.company_name;
    }
    return 'Conversation';
  }

  getLastMessagePreview(conversation: ConversationResponse): string {
    if (conversation.last_message) {
      const text = conversation.last_message.message_text;
      return text.length > 40 ? text.substring(0, 40) + '...' : text;
    }
    return 'No messages yet';
  }

  getTimeAgo(date: Date | string | null | undefined): string {
    if (!date) return '';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return past.toLocaleDateString();
  }

  formatMessageTime(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  isOwnMessage(message: MessageResponse): boolean {
    return message.sender_user_id.toString() === this.currentUserId;
  }

  getSenderName(message: MessageResponse): string {
    if (message.sender) {
      return message.sender.first_name + ' ' + message.sender.last_name;
    }
    return 'Unknown';
  }

  // Context helpers
  getConversationContext(conversation: ConversationResponse): 'event' | 'organization' | 'direct' {
    if (conversation.event_id || conversation.event) {
      return 'event';
    }
    if (conversation.company_id || conversation.company) {
      return 'organization';
    }
    return 'direct';
  }

  getContextIcon(conversation: ConversationResponse): string {
    const context = this.getConversationContext(conversation);
    switch (context) {
      case 'event': return 'fa-calendar-alt';
      case 'organization': return 'fa-building';
      default: return 'fa-user';
    }
  }

  getContextLabel(conversation: ConversationResponse): string {
    const context = this.getConversationContext(conversation);
    switch (context) {
      case 'event': return 'chat.contextEvent';
      case 'organization': return 'chat.contextOrganization';
      default: return 'chat.contextDirect';
    }
  }

  // Participant helpers
  getOtherParticipants(conversation: ConversationResponse): any[] {
    if (!conversation.participants) return [];
    return conversation.participants.filter(p => p.user_id.toString() !== this.currentUserId);
  }

  getParticipantName(participant: any): string {
    if (participant.user) {
      return participant.user.first_name + ' ' + participant.user.last_name;
    }
    return 'Unknown';
  }

  getParticipantInitials(participant: any): string {
    if (participant.user) {
      const first = participant.user.first_name?.charAt(0) || '';
      const last = participant.user.last_name?.charAt(0) || '';
      return (first + last).toUpperCase();
    }
    return '?';
  }

  getAllParticipantNames(conversation: ConversationResponse): string {
    const others = this.getOtherParticipants(conversation);
    if (others.length === 0) return '';
    return others.map(p => this.getParticipantName(p)).join(', ');
  }

  goBackToList() {
    this.selectedConversation = null;
    this.messages = [];
    this.stopMessagePolling();
  }
}
